import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff, Lock } from "lucide-react";
import { apiResetCheck, apiResetPassword } from "@/lib/api";
import { PasswordChecklist, passwordValid } from "@/components/PasswordChecklist";
import { SEO } from "@/components/SEO";

const INVALID_MSG = "Questo link non è valido o è scaduto. Richiedi un nuovo link per recuperare la password.";

// Il token arriva nel frammento (#token=...): non viene mai inviato a server, log o header Referer.
const readToken = () => new URLSearchParams(window.location.hash.slice(1)).get("token") || "";

function useNoReferrer() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "referrer"; m.content = "no-referrer";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
}

function PwField({ value, onChange, placeholder, testid, show, onToggle }) {
  return (
    <label className="relative block">
      <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-subtle" />
      <input
        type={show ? "text" : "password"} autoComplete="new-password" value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-surface border border-hair rounded-full pl-11 pr-11 py-3 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand"
      />
      <button type="button" onClick={onToggle} aria-label={show ? "Nascondi password" : "Mostra password"} data-testid={`${testid}-toggle`}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-subtle hover:text-brand">
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </label>
  );
}

function Invalid() {
  return (
    <div data-testid="reset-invalid" className="text-center">
      <AlertTriangle size={34} className="mx-auto text-bad mb-4" />
      <p className="text-sm text-onSurfaceSecondary leading-relaxed mb-6">{INVALID_MSG}</p>
      <Link to="/password-dimenticata" data-testid="reset-request-new" className="inline-flex px-5 py-2.5 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors">Recupera la password</Link>
    </div>
  );
}

export default function ResetPassword() {
  useNoReferrer();
  const [token] = useState(readToken);
  const [state, setState] = useState("checking");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.history.replaceState(null, "", window.location.pathname);
    if (!token) { setState("invalid"); return; }
    apiResetCheck(token).then((r) => setState(r.valid ? "form" : "invalid")).catch(() => setState("invalid"));
  }, [token]);

  const mismatch = pw2.length > 0 && pw !== pw2;
  const submit = async (e) => {
    e.preventDefault();
    if (!passwordValid(pw)) return setError("La password non rispetta i requisiti indicati");
    if (pw !== pw2) return setError("Le due password non coincidono");
    setBusy(true); setError("");
    try { await apiResetPassword(token, pw); setState("done"); }
    catch (err) { if (err.status === 400 && /link/i.test(err.message)) setState("invalid"); else setError(err.message || "Errore, riprova"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <SEO title="Imposta una nuova password" />
      <div className="bg-surfaceSecondary border border-hair rounded-2xl p-6 sm:p-8">
        {state === "checking" && <div className="flex justify-center py-8"><Loader2 size={26} className="animate-spin text-brand" /></div>}
        {state === "invalid" && <Invalid />}
        {state === "done" && (
          <div data-testid="reset-done" className="text-center">
            <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-4" />
            <p className="text-sm text-onSurface leading-relaxed mb-6">Password aggiornata correttamente. Ora puoi accedere con la nuova password.</p>
            <Link to="/login" data-testid="reset-go-login" className="inline-flex px-5 py-2.5 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors">Vai ad Accedi</Link>
          </div>
        )}
        {state === "form" && (
          <form onSubmit={submit} data-testid="reset-form" className="space-y-3">
            <h1 className="font-serif text-2xl font-semibold text-onSurface mb-2">Imposta una nuova password</h1>
            <input type="text" name="username" autoComplete="username" className="hidden" readOnly tabIndex={-1} aria-hidden />
            <PwField value={pw} onChange={(v) => { setPw(v); setError(""); }} placeholder="Nuova password" testid="reset-password" show={show} onToggle={() => setShow((s) => !s)} />
            <PasswordChecklist value={pw} testid="reset-password-rules" />
            <PwField value={pw2} onChange={(v) => { setPw2(v); setError(""); }} placeholder="Conferma nuova password" testid="reset-password-confirm" show={show} onToggle={() => setShow((s) => !s)} />
            {mismatch && <p data-testid="reset-mismatch" className="text-xs text-bad">Le due password non coincidono</p>}
            {error && <p data-testid="reset-error" className="text-xs text-bad">{error}</p>}
            <button type="submit" disabled={busy || !passwordValid(pw) || pw !== pw2} data-testid="reset-submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-brand text-onBrand font-semibold text-sm hover:bg-brandSecondary disabled:opacity-60 transition-colors">
              {busy && <Loader2 size={16} className="animate-spin" />} Salva nuova password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
